import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";

import { authApi } from "@/api/endpoints";
import { parseApiError } from "@/api/errors";
import type { AuthSession, BotLoginRequest } from "@/types/api";

export const BOT_POLL_INTERVAL_MS = 2_000;

/** Bu kodlar bilan so'rovni qayta so'rashdan foyda yo'q — yangisini boshlash kerak. */
const TERMINAL_CODES = new Set(["LOGIN_REQUEST_EXPIRED", "LOGIN_REQUEST_NOT_FOUND"]);

export type BotLoginPhase = "idle" | "waiting" | "expired";

/**
 * Bot orqali kirish: so'rov yaratiladi, admin botda «Start» ni bosadi, sahifa
 * esa tasdiqni so'rab turadi. Localhost'da Telegram Login Widget ishlamaydi,
 * bu yo'l esa har qanday domenda ishlaydi.
 */
export function useBotLogin(onConfirmed: (session: AuthSession) => void) {
  const [request, setRequest] = useState<BotLoginRequest | null>(null);
  const [phase, setPhase] = useState<BotLoginPhase>("idle");
  const onConfirmedRef = useRef(onConfirmed);

  useEffect(() => {
    onConfirmedRef.current = onConfirmed;
  }, [onConfirmed]);

  const start = useMutation({
    mutationFn: authApi.botLogin,
    onSuccess: created => {
      setRequest(created);
      setPhase("waiting");
    },
  });

  useEffect(() => {
    if (!request) return;
    let stopped = false;

    const stop = (next: BotLoginPhase) => {
      stopped = true;
      clearInterval(timer);
      setPhase(next);
      setRequest(null);
    };

    const tick = async () => {
      if (Date.now() > Date.parse(request.expiresAt)) return stop("expired");
      try {
        const result = await authApi.pollBotLogin(request.token);
        if (stopped) return;
        if (result.status === "CONFIRMED" && result.session) {
          stop("idle");
          onConfirmedRef.current(result.session);
        }
      } catch (error) {
        if (!stopped && TERMINAL_CODES.has(parseApiError(error).code)) stop("expired");
        // Boshqa xatolar (tarmoq uzilishi) — keyingi urinishda o'tib ketishi mumkin.
      }
    };

    const timer = setInterval(() => void tick(), BOT_POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [request]);

  const begin = useCallback(() => start.mutate(), [start]);

  return {
    phase,
    botUrl: request?.botUrl ?? null,
    begin,
    starting: start.isPending,
    startError: start.error,
  };
}
